import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LoadingButton } from '@mui/lab';

import {
  ControlledDatePicker,
  ControlledTextField
} from 'components/shared/ControlledFields';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import schema from './ProtocolForm.schema';
import { toProtocolFormValues } from './ProtocolForm.utils';
import { ValidityFields } from './ProtocolForm.styles';
import { useSaveProtocol } from './hooks/useSaveProtocol';

import type {
  ProtocolFormProps,
  ProtocolFormValues
} from './ProtocolForm.types';

/**
 * Name and validity of a protocol. There is deliberately no duration field: the duration follows
 * from the rules, and a field could contradict them. Messages only appear after a save attempt
 * (react-hook-form's default `onSubmit` mode), not while the form is still empty.
 */
const ProtocolForm = ({ protocol, onSaved }: ProtocolFormProps) => {
  'use memo';
  const { t } = useTranslation('common');
  const methods = useForm<ProtocolFormValues>({
    defaultValues: toProtocolFormValues(protocol),
    resolver: yupResolver(schema)
  });
  const { saveProtocol, isSaving } = useSaveProtocol(protocol, onSaved);
  const { control, handleSubmit } = methods;

  return (
    <MultiStepForm onSubmit={handleSubmit(saveProtocol)} methods={methods}>
      <MultiStepForm.Section title={t('adminPortal.protocols.form.section')}>
        <ControlledTextField
          control={control}
          name="name"
          label={t('adminPortal.protocols.name')}
          variant="outlined"
          required
          fullWidth
        />
        <ValidityFields>
          <ControlledDatePicker
            control={control}
            name="validFrom"
            label={t('adminPortal.protocols.validFrom')}
            required
            fullWidth
            showTodayButton
          />
          <ControlledDatePicker
            control={control}
            name="validUntil"
            label={t('adminPortal.protocols.validUntil')}
            fullWidth
            showTodayButton
          />
        </ValidityFields>
      </MultiStepForm.Section>
      <MultiStepForm.Actions>
        <LoadingButton
          loading={isSaving}
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
        >
          {t('save')}
        </LoadingButton>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};

export default ProtocolForm;
